import { Component, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { QueryErrorResetBoundary, useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { effectiveSearch, useDebouncedValue } from '@eureca/ui/forms';
import { APP_TIMEZONE } from '@/lib/timezone';
import { computeProgramDaysRemaining } from '@/features/programs/programs-list/program-row-format';
import type { ProgramStatus } from '@/features/programs/programs-list/ProgramsPage.component';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';
import { JobsPage as JobsPageComponent } from './JobsPage.component';
import type {
  JobListItem,
  JobAction,
  JobActiveDialog,
  JobStatus,
  JobStatusFilter,
  ProgramFilterOption,
} from './JobsPage.component';
import { jobsQueryOptions, type JobListEntry } from './jobs-query';
import { JOB_STATUS_TOKENS, PAGE_SIZE_OPTIONS, type JobsSearch } from './jobs-search.schema';
import { JobsErrorFallback } from './JobsStates';
import { jobProgramOptionsQueryOptions } from '@/features/jobs/job-create/job-program-options-query';
import { getKeycloak } from '@/auth/keycloak-instance';
import { useDuplicateJob, useTransitionJobStatus } from './jobs-list-mutations';
import { JobMutationError } from '@/features/jobs/job-shared/job-edit-mutation';
import { recruiterEnv } from '@/config/env';
import { buildCandidateJobUrl, copyPrivateLink, openPrivateLink } from '@/lib/candidate-links';

const ROWS_PER_PAGE_OPTIONS = [...PAGE_SIZE_OPTIONS];
const SEARCH_DEBOUNCE_MS = 300;

function logoutToLogin(): void {
  getKeycloak().logout({ redirectUri: `${window.location.origin}/login` });
}

/**
 * Format a UTC instant (ISO 8601, `timestamptz`) into the recruiter list
 * `dd/MM/yy` in America/Sao_Paulo — an evening-BRT deadline is the next UTC day,
 * so the calendar day must be read in the project timezone, not UTC.
 */
const DATE_BR_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  timeZone: APP_TIMEZONE,
});

function formatBrDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return DATE_BR_FORMAT.format(d);
}

/**
 * EU-424 — the job and program status enums diverge on the "private" and
 * terminal values; translate here so we can reuse `computeProgramDaysRemaining`
 * (which only counts the deadline for active statuses) instead of duplicating
 * the Programs list math.
 */
function jobToProgramStatus(status: JobStatus): ProgramStatus {
  switch (status) {
    case 'ativa':
      return 'ativa';
    case 'ativa-privada':
      return 'ativo-privado';
    case 'rascunho':
      return 'rascunho';
    case 'encerrada':
      return 'encerrado';
    case 'cancelada':
      return 'cancelado';
  }
}

/** Janela de inscrição do programa vinculado (datas-fonte das colunas Início/Fim). */
interface ProgramWindow {
  startApplying: string | null;
  endApplying: string | null;
}

/**
 * A vaga não tem datas próprias — quem controla o ciclo é o programa. As
 * colunas Início/Fim espelham a janela de inscrição do programa vinculado
 * (`startApplying`/`endApplying`), não mais campos da própria vaga. Sem o
 * programa no mapa (fora da página de 100 opções carregadas, ou janela nula)
 * a célula colapsa para "—".
 */
function mapEntry(entry: JobListEntry, programWindow: ProgramWindow | undefined): JobListItem {
  const status = entry.status as JobStatus;
  const startDate = programWindow?.startApplying ?? null;
  const endDate = programWindow?.endApplying ?? null;
  return {
    id: entry.id,
    name: entry.name,
    programId: entry.programId,
    programName: entry.programName,
    status,
    registrations: entry.registrations,
    finalPhaseCompletions: entry.finalPhaseCompletions,
    startDate: formatBrDate(startDate),
    endDaysRemaining: computeProgramDaysRemaining(endDate, jobToProgramStatus(status), new Date()),
    endDate: endDate ? formatBrDate(endDate) : '—',
  };
}

/**
 * Error boundary that distinguishes 403 (no access) from generic failures.
 * Companies has a richer NoAccessFallback; first pass for jobs reuses the
 * same retry-or-logout treatment for simplicity.
 */
class RetryableBoundary extends Component<
  { children: ReactNode; onReset: () => void },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(err: unknown): { error: Error } {
    return { error: err instanceof Error ? err : new Error(String(err)) };
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onReset();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <JobsErrorFallback
          onRetry={this.handleRetry}
          onLogout={logoutToLogin}
          message={this.state.error.message}
        />
      );
    }
    return this.props.children;
  }
}

function JobsPageContent({ search }: { search: JobsSearch }) {
  const navigate = useNavigate();
  // Program filter options come from the BFF program list, NOT from the jobs on
  // the current page. DEV-315 — ESCOPADAS pela empresa da área de trabalho
  // (`companyId`): sem isso o BFF/Core devolvem todo programa do escopo do
  // consultor e o filtro oferecia programas de outras empresas (AC-1).
  const programOptionsQuery = useQuery(jobProgramOptionsQueryOptions(search.companyId));
  const programOptionItems = programOptionsQuery.data?.items;
  const selectedProgramId = search.programId ?? null;
  // Um `programId` que não pertence à empresa atual (empresa trocada, link
  // antigo, programa alheio) é descartado — nunca busca vagas dele (AC-2).
  // Enquanto as opções carregam, o programa da URL vale (otimista); se depois
  // não estiver na lista, o efeito abaixo limpa a URL.
  const programInScope =
    selectedProgramId !== null &&
    (programOptionItems === undefined ||
      programOptionItems.some((p) => p.id === selectedProgramId));
  const effectiveProgramId = programInScope ? selectedProgramId : null;
  // DEV-315 AC-4 — sem programa efetivo NÃO há busca de vagas (`enabled`),
  // nem no load: a tela pede a escolha em vez de pré-carregar a empresa inteira.
  const query = useQuery({
    ...jobsQueryOptions(search),
    enabled: effectiveProgramId !== null,
  });
  // Popover do filtro Programa — controlado aqui para fechar ao escolher e
  // para o placeholder "Selecione um programa" conseguir abri-lo.
  const [programFilterOpen, setProgramFilterOpen] = useState(false);

  const [activeDialog, setActiveDialog] = useState<JobActiveDialog>(null);
  const [targetJobId, setTargetJobId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionInFlight, setActionInFlight] = useState(false);
  const transitionStatus = useTransitionJobStatus();
  const duplicateJob = useDuplicateJob();
  // Keep the raw search input local so each keystroke renders instantly, then
  // push only the DEBOUNCED term into the URL (canonical state). A burst of
  // keystrokes collapses into one navigation; TanStack Query auto-cancels the
  // prior in-flight fetch when the queryKey changes. See
  // `.claude/rules/server-side-search.md`.
  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const next = effectiveSearch(debouncedSearch);
    if (next === search.search) return;
    void navigate({
      to: '/vagas',
      search: (prev) => ({ ...(prev as JobsSearch), search: next, page: 1 }),
    });
  }, [debouncedSearch, search.search, navigate]);

  // DEV-315 AC-2 — programa fora da lista da empresa atual sai da URL (replace:
  // não vira entrada de histórico) e a tela volta ao placeholder.
  useEffect(() => {
    if (selectedProgramId === null || programOptionItems === undefined) return;
    if (programOptionItems.some((p) => p.id === selectedProgramId)) return;
    void navigate({
      to: '/vagas',
      replace: true,
      search: (prev) => ({ ...(prev as JobsSearch), programId: undefined, page: 1 }),
    });
  }, [selectedProgramId, programOptionItems, navigate]);

  if (query.error) throw query.error;
  // VPG-011 (retorno da QA) — enquanto as vagas carregam a tela NÃO é trocada
  // pelo fallback inteiro: a barra de filtros fica montada e interativa e só a
  // área da lista mostra o esqueleto. Trocar de programa no meio do
  // carregamento muda a queryKey; a resposta atrasada do programa anterior cai
  // no cache dele e nunca é exibida (`placeholderData` só reaproveita dados do
  // MESMO programa — FR-7).
  const jobsLoading = effectiveProgramId !== null && !query.data;

  // Sem programa efetivo a listagem é vazia por definição (nenhuma busca saiu).
  const items = effectiveProgramId !== null ? (query.data?.items ?? []) : [];
  const total = effectiveProgramId !== null ? (query.data?.total ?? 0) : 0;
  const page = query.data?.page ?? search.page;
  const pageSize = query.data?.pageSize ?? search.pageSize;
  // Janela de inscrição por programa (Início/Fim das vagas espelham o programa
  // vinculado). Reaproveita a query de programas já carregada para o filtro —
  // mesmos itens, sem request extra. A vaga cujo programa não está nesta página
  // de 100 opções cai no "—" até o programa ser carregado.
  const programWindowById = new Map<string, ProgramWindow>(
    (programOptionsQuery.data?.items ?? []).map((p) => [
      p.id,
      {
        startApplying: (p as { startApplying?: string | null }).startApplying ?? null,
        endApplying: (p as { endApplying?: string | null }).endApplying ?? null,
      },
    ])
  );
  const jobs: JobListItem[] = items.map((entry) =>
    mapEntry(entry, programWindowById.get(entry.programId))
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Distinct programs the consultor has access to (from `GET /programs`,
  // pageSize=100). Sorted alphabetically PT-BR. Falls back to an empty list
  // while the query is loading so the filter button stays clickable but
  // shows "Nenhum programa encontrado" until the query resolves.
  const programOptions: ProgramFilterOption[] = (programOptionsQuery.data?.items ?? [])
    .map((p) => ({ id: p.id, name: p.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const selectedStatuses = (search.status ?? []) as JobStatusFilter[];
  // O programa é obrigatório, não um filtro: só Status (e a busca) contam como
  // "filtro ativo" para os estados vazios da listagem.
  const hasActiveFilters = selectedStatuses.length > 0;

  function pushSearch(updater: (prev: JobsSearch) => JobsSearch) {
    void navigate({
      to: '/vagas',
      search: (prev) => updater(prev as JobsSearch),
    });
  }

  function handleSearchChange(value: string) {
    // Local-only render; the debounced effect above owns the URL push.
    setSearchInput(value);
    setSelectedIds([]);
  }

  function handleRowsPerPageChange(rows: number) {
    if (!(ROWS_PER_PAGE_OPTIONS as number[]).includes(rows)) return;
    setSelectedIds([]);
    pushSearch((prev) => ({
      ...prev,
      pageSize: rows as JobsSearch['pageSize'],
      page: 1,
    }));
  }

  function handleSortToggle() {
    pushSearch((prev) => ({
      ...prev,
      sort: prev.sort ?? 'name',
      dir: prev.dir === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }

  // DEV-315 AC-5 — escolher um programa SUBSTITUI o anterior (um por vez) e
  // volta para a página 1; a busca sai só para esse programa.
  function handleProgramSelect(programId: string) {
    setSelectedIds([]);
    setProgramFilterOpen(false);
    pushSearch((prev) => ({ ...prev, programId, page: 1 }));
  }

  function handleStatusFilterToggle(status: JobStatusFilter) {
    if (!JOB_STATUS_TOKENS.includes(status)) return;
    setSelectedIds([]);
    pushSearch((prev) => {
      const current = (prev.status ?? []) as JobStatusFilter[];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      return { ...prev, status: next.length > 0 ? next : undefined, page: 1 };
    });
  }

  function handleClearFilters() {
    setSelectedIds([]);
    setSearchInput('');
    pushSearch((prev) => ({
      ...prev,
      search: undefined,
      programId: undefined,
      status: undefined,
      page: 1,
    }));
  }

  function handlePageChange(nextPage: number) {
    setSelectedIds([]);
    pushSearch((prev) => ({ ...prev, page: nextPage }));
  }

  function handleCreateJob() {
    void navigate({ to: '/vagas/nova' });
  }

  function handleOpenJob(id: string) {
    // EU-560 — o clique no nome abre a "Gestão de vaga" (hub de visão + ações),
    // não mais o hub de edição direto.
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId: id },
    });
  }

  async function handleJobAction(action: JobAction, id: string) {
    setTargetJobId(id);
    if (action === 'end') {
      setActiveDialog('end');
      return;
    }
    if (action === 'cancel') {
      setActiveDialog('cancel');
      return;
    }
    // EU-244 — a ação de publicar/ativar vaga isoladamente foi removida; a
    // publicação acontece apenas no nível do programa.
    if (action === 'open-private-link') {
      openPrivateLink(buildCandidateJobUrl(recruiterEnv.candidateAppUrl, id));
      return;
    }
    if (action === 'copy-private-link') {
      void copyPrivateLink(
        buildCandidateJobUrl(recruiterEnv.candidateAppUrl, id),
        'Link privado da vaga'
      );
      return;
    }
    if (action === 'edit') {
      // EU-560 — "Editar" no menu ⋮ também leva à Gestão de vaga; a edição das
      // seções acontece pelo painel "Dados" de lá.
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId: id },
      });
      return;
    }
    if (action === 'duplicate') {
      if (actionInFlight) return;
      setActionInFlight(true);
      try {
        const cloned = await duplicateJob.mutateAsync({ id });
        toast('Vaga duplicada.', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
        // A cópia abre direto na Gestão de vaga (o hub /editar virou redirect).
        await navigate({
          to: '/vagas/$jobId',
          params: { jobId: cloned.id },
        });
      } catch (err) {
        const status = err instanceof JobMutationError ? err.status : 0;
        if (status === 404) {
          toast.error('Vaga não encontrada.');
        } else if (status === 401 || status === 403) {
          toast.error('Você não tem permissão para duplicar essa vaga.');
        } else {
          toast.error('Não foi possível duplicar a vaga. Tente novamente.');
        }
      } finally {
        setActionInFlight(false);
      }
    }
  }

  function handleDialogOpenChange(open: boolean) {
    if (actionInFlight) return;
    if (!open) {
      setActiveDialog(null);
      setTargetJobId(null);
    }
  }

  /**
   * Single-row status transition. Distinguishes 422 invalid_transition
   * (impossible from the current row state — show a specific toast) from
   * the generic error path so the operator can act on the message.
   */
  async function transitionSingle(target: 'cancelada' | 'encerrada', successMessage: string) {
    if (!targetJobId) {
      toast.error('Nenhuma vaga selecionada.');
      return;
    }
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      await transitionStatus.mutateAsync({ id: targetJobId, status: target });
      setActiveDialog(null);
      setTargetJobId(null);
      toast(successMessage, { action: { label: 'Fechar', onClick: () => undefined } });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 422) {
        toast.error('Essa vaga não pode mais ser alterada.');
      } else if (status === 404) {
        toast.error('Vaga não encontrada.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para essa ação.');
      } else {
        toast.error('Não foi possível atualizar o status. Tente novamente.');
      }
    } finally {
      setActionInFlight(false);
    }
  }

  function handleConfirmEnd() {
    void transitionSingle('encerrada', 'Vaga encerrada.');
  }

  function handleConfirmCancel() {
    void transitionSingle('cancelada', 'Vaga cancelada.');
  }

  function handleToggleSelectAll() {
    const pageIds = jobs.map((j) => j.id);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  }

  function handleToggleSelectRow(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  }

  function handleClearSelection() {
    setSelectedIds([]);
  }

  function handleBulkAction(action: 'end' | 'cancel') {
    setActiveDialog(action === 'end' ? 'bulk-end' : 'bulk-cancel');
  }

  /**
   * Bulk status transition. The BFF doesn't expose a bulk endpoint — we
   * fan out the per-row mutation with `Promise.allSettled` so a single
   * 422 (invalid transition for an already-terminal row) doesn't block the
   * remaining rows. Reports the aggregate (X concluído / Y falhou) so the
   * operator knows what happened.
   */
  async function transitionBulk(target: 'cancelada' | 'encerrada', verb: string) {
    if (selectedIds.length === 0) {
      toast.error('Nenhuma vaga selecionada.');
      return;
    }
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => transitionStatus.mutateAsync({ id, status: target }))
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      setActiveDialog(null);
      setSelectedIds([]);
      // Gender agreement: "encerrada/encerradas", "cancelada/canceladas".
      const okPlural = ok === 1 ? '' : 's';
      const okPhrase = `${ok} vaga${okPlural} ${verb}${okPlural}`;
      if (fail === 0) {
        toast(`${okPhrase}.`, {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      } else if (ok === 0) {
        toast.error(`Nenhuma vaga pôde ser ${verb}.`);
      } else {
        toast(`${okPhrase}, ${fail} não ${fail === 1 ? 'pôde' : 'puderam'} ser ${verb}.`, {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      }
    } finally {
      setActionInFlight(false);
    }
  }

  function handleConfirmBulkEnd() {
    void transitionBulk('encerrada', 'encerrada');
  }

  function handleConfirmBulkCancel() {
    void transitionBulk('cancelada', 'cancelada');
  }

  return (
    <JobsPageComponent
      jobs={jobs}
      brand={search.companyId ? <WorkspaceBrand companyId={search.companyId} /> : undefined}
      totalCount={total}
      searchQuery={searchInput}
      onSearchChange={handleSearchChange}
      onCreateJob={handleCreateJob}
      onSortToggle={handleSortToggle}
      onOpenJob={handleOpenJob}
      onJobAction={handleJobAction}
      programOptions={programOptions}
      selectedProgramId={effectiveProgramId}
      jobsLoading={jobsLoading}
      onProgramSelect={handleProgramSelect}
      programFilterOpen={programFilterOpen}
      onProgramFilterOpenChange={setProgramFilterOpen}
      selectedStatuses={selectedStatuses}
      onStatusFilterToggle={handleStatusFilterToggle}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      rowsPerPage={pageSize}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      onRowsPerPageChange={handleRowsPerPageChange}
      page={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      selectedIds={selectedIds}
      onToggleSelectAll={handleToggleSelectAll}
      onToggleSelectRow={handleToggleSelectRow}
      onClearSelection={handleClearSelection}
      onBulkAction={handleBulkAction}
      activeDialog={activeDialog}
      onDialogOpenChange={handleDialogOpenChange}
      onConfirmEnd={handleConfirmEnd}
      onConfirmCancel={handleConfirmCancel}
      onConfirmBulkEnd={handleConfirmBulkEnd}
      onConfirmBulkCancel={handleConfirmBulkCancel}
    />
  );
}

export default function JobsPage() {
  const search = useSearch({ from: '/_authenticated/vagas' }) as JobsSearch;

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <RetryableBoundary onReset={reset}>
          <JobsPageContent search={search} />
        </RetryableBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
